import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { usePageMeta } from "@/hooks/usePageMeta";
import { getArticleBySlug, articles } from "@/mocks/articles";

function getArticleSiteUrl(): string {
  if (typeof import.meta !== "undefined" && import.meta.env?.VITE_SITE_URL) {
    return import.meta.env.VITE_SITE_URL;
  }
  return "https://oopbuyspreadsheet.ink";
}

function buildArticleJsonLd(
  article: ReturnType<typeof getArticleBySlug>,
  siteUrl: string,
  slug: string,
) {
  if (!article) return [];
  const articleUrl = `${siteUrl}/article/${slug}`;

  const result: Record<string, unknown>[] = [
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item: siteUrl + "/",
        },
        {
          "@type": "ListItem",
          position: 2,
          name: article.title,
          item: articleUrl,
        },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      headline: article.title,
      description: article.intro.substring(0, 160),
      author: {
        "@type": "Organization",
        name: "OOPBuy Spreadsheet",
        url: siteUrl + "/",
      },
      publisher: {
        "@type": "Organization",
        name: "OOPBuy Spreadsheet",
        url: siteUrl + "/",
      },
      datePublished: "2025-06-15",
      dateModified: "2025-06-15",
      mainEntityOfPage: {
        "@type": "WebPage",
        "@id": articleUrl,
      },
      image: article.imageUrl,
    },
  ];

  if (article.faqs && article.faqs.length > 0) {
    result.push({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: article.faqs.map((faq) => ({
        "@type": "Question",
        name: faq.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: faq.answer,
        },
      })),
    });
  }

  return result;
}

function buildArticleKeywords(article: ReturnType<typeof getArticleBySlug>): string {
  if (!article) return "oopbuy spreadsheet";
  const base = "oopbuy spreadsheet";
  const category = article.categoryLabel.toLowerCase();
  const titleWords = article.title
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, "")
    .split(/\s+/)
    .filter((w) => w.length > 3 && w !== "oopbuy" && w !== "spreadsheet")
    .slice(0, 4)
    .join(", ");
  return `${base}, ${category}, ${titleWords}`;
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? getArticleBySlug(slug) : undefined;
  const siteUrl = getArticleSiteUrl();

  const is404 = !article;
  const metaTitle = is404
    ? "Article Not Found | OOPBuy Spreadsheet"
    : article.title + " | OOPBuy Spreadsheet";
  const metaDescription = is404
    ? "The article you are looking for does not exist. Browse our complete library of OOPBuy spreadsheet guides and product comparison articles."
    : article.intro.substring(0, 160);
  const metaKeywords = is404 ? "" : buildArticleKeywords(article);
  const metaCanonicalPath = "/article/" + (slug || "");
  const metaJsonLd = is404 ? [] : buildArticleJsonLd(article, siteUrl, slug!);
  const metaOgImage = is404 ? undefined : article.imageUrl;

  usePageMeta({
    title: metaTitle,
    description: metaDescription,
    keywords: metaKeywords || undefined,
    canonicalPath: metaCanonicalPath,
    ogType: is404 ? "website" : "article",
    ogImage: metaOgImage,
    jsonLd: metaJsonLd,
    noIndex: is404,
  });

  if (is404) {
    return (
      <main>
        <Navbar />
        <div className="pt-32 pb-20 bg-background-50 min-h-[60vh]">
          <div className="mx-auto px-6 md:px-10 max-w-3xl text-center">
            <h1 className="text-3xl font-bold font-heading text-foreground-950">
              Article Not Found
            </h1>
            <p className="mt-4 text-foreground-500">
              The article you are looking for does not exist.
            </p>
            <Link
              to="/"
              className="mt-6 inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-white rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              Back to Home
            </Link>
          </div>
        </div>
        <Footer />
      </main>
    );
  }

  return (
    <main>
      <Navbar />

      <article className="pt-24 pb-14 md:pb-20 bg-background-50">
        <div className="mx-auto px-6 md:px-10 max-w-3xl">
          <nav className="mb-8" aria-label="Breadcrumb">
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 text-sm text-foreground-500 hover:text-primary-600 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              Back to OOPBuy Spreadsheet Home
            </Link>
          </nav>

          <header className="mb-10">
            <span className="inline-block px-3 py-1 bg-accent-100 text-accent-700 text-xs font-semibold rounded-full mb-4">
              {article.categoryLabel}
            </span>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold font-heading text-foreground-950 leading-tight">
              {article.title}
            </h1>
            <p className="mt-4 text-base md:text-lg text-foreground-600 leading-relaxed">
              {article.intro}
            </p>
            <div className="mt-5 flex items-center gap-4 text-xs text-foreground-400">
              <span>Updated: June 2025</span>
              <span>&middot;</span>
              <span>{article.readTime}</span>
            </div>
          </header>

          <div className="mb-10 rounded-2xl overflow-hidden">
            <img
              src={article.imageUrl}
              alt={article.title}
              className="w-full aspect-[2/1] object-cover object-center"
            />
          </div>

          <div className="prose-custom text-foreground-800 leading-relaxed space-y-6">
            {article.sections.map((section, sIdx) => (
              <div key={sIdx}>
                <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 mt-10 mb-4">
                  {section.heading}
                </h2>
                {section.paragraphs.map((p, pIdx) => (
                  <p key={pIdx} className="text-base text-foreground-700 mb-4">
                    {p}
                  </p>
                ))}
              </div>
            ))}

            {article.tables.map((table, tIdx) => (
              <div
                key={tIdx}
                className="my-8 bg-background-100 rounded-xl p-5 md:p-7 border border-background-200"
              >
                <h3 className="text-lg font-bold font-heading text-foreground-900 mb-4">
                  {table.title}
                </h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-background-300">
                        {table.headers.map((h, hIdx) => (
                          <th
                            key={hIdx}
                            className="text-left py-2.5 px-3 text-foreground-800 font-semibold whitespace-nowrap"
                          >
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {table.rows.map((row, rIdx) => (
                        <tr
                          key={rIdx}
                          className="border-b border-background-200 last:border-b-0"
                        >
                          {row.map((cell, cIdx) => (
                            <td
                              key={cIdx}
                              className={`py-2.5 px-3 text-foreground-700 ${cIdx === 0 ? "font-medium" : ""}`}
                            >
                              {cell}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}

            <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 mt-10 mb-4">
              Frequently Asked Questions
            </h2>
            <div className="space-y-4">
              {article.faqs.map((faq, fIdx) => (
                <div key={fIdx} className="bg-background-100 rounded-lg p-5">
                  <h3 className="text-base font-semibold text-foreground-900 mb-2">
                    {faq.question}
                  </h3>
                  <p className="text-sm text-foreground-600">{faq.answer}</p>
                </div>
              ))}
            </div>

            {article.relatedArticles.length > 0 && (
              <>
                <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 mt-10 mb-4">
                  Related Articles
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {article.relatedArticles.map((ra) => (
                    <Link
                      key={ra.slug}
                      to={`/article/${ra.slug}`}
                      className="block px-4 py-3 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-700 hover:border-accent-300 hover:text-accent-700 transition-all cursor-pointer"
                    >
                      {ra.title}
                    </Link>
                  ))}
                </div>
              </>
            )}

            <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 mt-10 mb-4">
              Explore All Product Categories
            </h2>
            <p className="text-base text-foreground-700 mb-4">
              Browse our complete range of product categories to find exactly
              what you are looking for:
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {[
                {
                  name: "Shoes",
                  url: "https://kaspreadsheet.com/category-911-Shoes.html",
                },
                {
                  name: "Hoodies / Sweaters",
                  url: "https://kaspreadsheet.com/category-912-Hoodies%2FSweaters.html",
                },
                {
                  name: "T-Shirts",
                  url: "https://kaspreadsheet.com/category-913-T-Shirts.html",
                },
                {
                  name: "Jackets",
                  url: "https://kaspreadsheet.com/category-914-Jackets.html",
                },
                {
                  name: "Pants / Shorts",
                  url: "https://kaspreadsheet.com/category-915-Pants%2FShorts.html",
                },
                {
                  name: "Headwear",
                  url: "https://kaspreadsheet.com/category-916-Headwear.html",
                },
                {
                  name: "Sets",
                  url: "https://kaspreadsheet.com/category-917-Sets.html",
                },
                {
                  name: "Underwear / Underpants",
                  url: "https://kaspreadsheet.com/category-918-underwear%2Funderpants.html",
                },
                {
                  name: "Jersey",
                  url: "https://kaspreadsheet.com/category-919-Jersey.html",
                },
                {
                  name: "Accessories",
                  url: "https://kaspreadsheet.com/category-920-Accessories.html",
                },
                {
                  name: "Bags",
                  url: "https://kaspreadsheet.com/category-921-Bags.html",
                },
                {
                  name: "Belts",
                  url: "https://kaspreadsheet.com/search-922/name/Bags.html",
                },
                {
                  name: "Perfumes",
                  url: "https://kaspreadsheet.com/search-923/name/Bags.html",
                },
                {
                  name: "Other Stuff",
                  url: "https://kaspreadsheet.com/search-924/name/Bags.html",
                },
              ].map((cat) => (
                <a
                  key={cat.name}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="block px-4 py-3 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-700 hover:border-accent-300 hover:text-accent-700 transition-all cursor-pointer"
                >
                  {cat.name}
                </a>
              ))}
            </div>
          </div>

          <div className="mt-12 pt-8 border-t border-background-200">
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary-600 hover:text-primary-700 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              Back to OOPBuy Spreadsheet Home
            </Link>
          </div>
        </div>
      </article>

      <Footer />
    </main>
  );
}